import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { Link } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, IconButton, Drawer, List, ListItem, ListItemText, Divider } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import ProviderSettings from './ProviderSettings';

const useStyles = makeStyles(theme => ({
  appBar: {
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(135deg, #FF0000 0%, #FF6B00 25%, #FFD700 50%, #FF6B00 75%, #FF0000 100%)',
    backgroundSize: '200% 200%',
    animation: '$pulse 3s ease-in-out 2',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3), inset 0 0 0 2px rgba(255, 255, 255, 0.2)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.1) 4px, rgba(0,0,0,0.1) 8px)',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle, transparent 1px, rgba(0,0,0,0.05) 1px)',
      backgroundSize: '6px 6px',
      pointerEvents: 'none',
    },
  },
  '@keyframes pulse': {
    '0%, 100%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
  },
  toolbar: {
    minHeight: '96px',
    padding: theme.spacing(0, 3),
    position: 'relative',
    zIndex: 1,
  },
  title: {
    flexGrow: 1,
    fontWeight: 900,
    fontSize: '2.5rem',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    textShadow: '3px 3px 0px rgba(0,0,0,0.3), 6px 6px 0px rgba(0,0,0,0.1)',
    color: '#FFFFFF',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.75rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.4rem',
      letterSpacing: '1px',
    },
  },
  desktopNav: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileMenuButton: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  button: {
    borderRadius: '8px',
    textTransform: 'uppercase',
    fontWeight: 700,
    padding: '8px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: '#FF0000',
    border: '2px solid rgba(0, 0, 0, 0.3)',
    boxShadow: '2px 2px 0px rgba(0, 0, 0, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-2px)',
      boxShadow: '3px 3px 0px rgba(0, 0, 0, 0.3)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      color: 'rgba(0, 0, 0, 0.3)',
      border: '2px solid rgba(0, 0, 0, 0.1)',
    },
  },
  editButton: {
    backgroundColor: '#FFD700',
    color: '#000000',
    border: '2px solid rgba(0, 0, 0, 0.4)',
    '&:hover': {
      backgroundColor: '#FFED4E',
      transform: 'translateY(-2px)',
    },
  },
  logoutButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#FFFFFF',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      transform: 'translateY(-2px)',
    },
  },
  loginContainer: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  userInfo: {
    marginRight: theme.spacing(1),
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  drawer: {
    width: 280,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #FF0000 0%, #FF6B00 50%, #FFD700 100%)',
    color: 'white',
  },
  drawerTitle: {
    fontWeight: 700,
    fontSize: '1.25rem',
  },
  listItem: {
    padding: theme.spacing(2),
    '&:hover': {
      backgroundColor: 'rgba(102, 126, 234, 0.08)',
    },
  },
  listItemText: {
    '& .MuiListItemText-primary': {
      fontWeight: 600,
      color: '#333',
    },
  },
}));

export default function ContainedButtons(props) {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    // Initialize FirebaseUI when component mounts and user is not logged in
    if (!props.appuser && props.ui) {
      // Wait a bit for the DOM to be ready
      setTimeout(() => {
        const container = document.getElementById('firebaseui-auth-container');
        if (container && !container.hasChildNodes()) {
          props.ui.start('#firebaseui-auth-container', props.uiConfig);
        }
      }, 100);
    }
  }, [props.appuser, props.ui, props.uiConfig]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const navItems = [
    { label: 'Heroes', to: '/', disabled: false },
    { label: 'Quick Entry', to: '/quickentry/', disabled: false },
    { label: 'Series', to: props.showSeries ? '/comicseries/' : '', disabled: !props.showSeries },
    { label: 'Comics', to: props.showComics ? '/series/' : '', disabled: !props.showComics },
    { label: 'Gap Issues', to: '/missing/', disabled: false, onClick: props.loadGapIssues },
    { label: 'New Releases', to: '/releases/', disabled: false },
  ];

  return (
    <>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6" className={classes.title}>
            Collection Ninja
          </Typography>

          {props.appuser && (
            <>
              {/* Desktop Navigation */}
              <Box className={classes.desktopNav}>
                <ProviderSettings />
                {navItems.map((item) => (
                  <Link key={item.label} to={item.to} style={{ textDecoration: 'none' }}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      disabled={item.disabled}
                      className={classes.button}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </Button>
                  </Link>
                ))}
                <Button 
                  size="small" 
                  variant="contained" 
                  disabled={!props.showComics && !props.showSeries}
                  className={`${classes.button} ${props.editMode ? classes.editButton : ''}`}
                  onClick={props.toggleEditMode}
                >
                  {props.editMode ? "Edit" : "View"}
                </Button>
                
                <Box className={classes.loginContainer}>
                  <Typography variant="body2" component="span" className={classes.userInfo}>
                    {props.appuser.email}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="contained" 
                    className={`${classes.button} ${classes.logoutButton}`}
                    onClick={props.onLogout}
                  >
                    Logout
                  </Button>
                </Box>
              </Box>

              {/* Mobile Menu Button */}
              <IconButton
                color="inherit"
                aria-label="open menu"
                edge="end"
                onClick={handleDrawerToggle}
                className={classes.mobileMenuButton}
              >
                <MenuIcon />
              </IconButton>
            </>
          )}

          {!props.appuser && (
            <Box className={classes.loginContainer}>
              <div id="firebaseui-auth-container"></div>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      {props.appuser && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerClose}
          classes={{ paper: classes.drawer }}
        >
          <Box className={classes.drawerHeader}>
            <Typography variant="h6" className={classes.drawerTitle}>
              Menu
            </Typography>
            <IconButton color="inherit" onClick={handleDrawerClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List>
            <ListItem className={classes.listItem}>
              <ProviderSettings />
            </ListItem>
            <Divider />
            <ListItem className={classes.listItem}>
              <ListItemText 
                primary={props.appuser.email}
                className={classes.listItemText}
                primaryTypographyProps={{ variant: 'body2', style: { fontWeight: 500, color: '#667eea' } }}
              />
            </ListItem>
            <Divider />
            {navItems.map((item) => (
              <Link 
                key={item.label} 
                to={item.to} 
                style={{ textDecoration: 'none' }}
                onClick={() => {
                  if (item.onClick) item.onClick();
                  handleDrawerClose();
                }}
              >
                <ListItem button className={classes.listItem} disabled={item.disabled}>
                  <ListItemText primary={item.label} className={classes.listItemText} />
                </ListItem>
              </Link>
            ))}
            <ListItem 
              button 
              className={classes.listItem}
              disabled={!props.showComics && !props.showSeries}
              onClick={() => {
                props.toggleEditMode();
                handleDrawerClose();
              }}
            >
              <ListItemText 
                primary={props.editMode ? "Edit Mode" : "View Mode"} 
                className={classes.listItemText}
              />
            </ListItem>
            <Divider />
            <ListItem 
              button 
              className={classes.listItem}
              onClick={() => {
                props.onLogout();
                handleDrawerClose();
              }}
            >
              <ListItemText 
                primary="Logout" 
                className={classes.listItemText}
                primaryTypographyProps={{ style: { color: '#d32f2f' } }}
              />
            </ListItem>
          </List>
        </Drawer>
      )}
    </>
  );
}